import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Card } from 'pokemon-tcg-sdk-typescript';
import { Shield, Zap, ArrowDownToLine, ZoomIn, Sword, MousePointer, MousePointerClick, ChevronDown } from 'lucide-react';
import { HPIcon } from './icons/HPIcon';
import CardZoom from './CardZoom';
import { slugify } from '../utils/slugify';
import { useCurrencyStore } from '../lib/currencyStore';
import { formatPrice } from '../utils/formatPrice';
import { EnergyIcon } from './icons/EnergyIcon';
import CollectionButton from './CollectionButton';
import CardImage from './CardImage';
import { US, EU } from 'country-flag-icons/react/3x2';

interface CardDetailsProps {
  card: Card;
}

export default function CardDetails({ card }: CardDetailsProps) {
  const { currency } = useCurrencyStore();
  const [isZoomed, setIsZoomed] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);

  if (!card) {
    return null;
  }

  // Get image URLs from the card data
  const imageUrl = card.image_large_url || 
    `https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images-webp/pokemon_cards/${card.id}_large.webp`;

  // Format date for price updates
  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to check if a price section should be displayed
  const shouldShowPriceSection = (prices: Record<string, number | null | undefined>) => {
    return Object.values(prices).some(price => price && price > 0);
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 text-white">
      <div className="flex flex-col lg:flex-row lg:gap-6 max-w-[1920px] mx-auto">
        {/* Card Details Section - Now First */}
        <div className="flex-1 order-2 lg:order-none">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
            <h2 className="text-2xl font-bold">{card.name}</h2>
            {card.types?.[0] && <EnergyIcon type={card.types[0]} />}
            {card.hp && (
              <div className="flex items-center gap-1">
                <HPIcon className="text-red-500" size={20} />
                <span className="font-semibold">{card.hp} HP</span>
              </div>
            )}
          </div>

          {/* Card Information Section */}
          <div className="relative bg-black/40 backdrop-blur-sm border border-white/10 p-4 rounded-lg mb-6 overflow-hidden">
            {/* Set Logo Background */}
            {card.set.images?.logo && (
              <div 
                className="absolute inset-0 opacity-5 pointer-events-none"
                style={{
                  backgroundImage: `url(${card.set.images.logo})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  transform: 'scale(0.65)'
                }}
              />
            )}
            <div className="relative z-10">
              <h3 className="text-lg font-semibold mb-4 text-white">Card Details</h3>
              {card.flavor && (
                <p className="italic text-white/80 mb-4">{card.flavor}</p>
              )}
              <div className="space-y-3">
                <p>
                  <span className="font-semibold text-gray-400">Set:</span>{' '}
                  <Link
                    to={`/${slugify(card.set.series)}/${slugify(card.set.name)}`}
                    className="text-white hover:text-white/80 transition-colors underline"
                  >
                    {card.set.name}
                  </Link>
                </p>
                <p>
                  <span className="font-semibold text-gray-400">Series:</span>{' '}
                  <Link
                    to={`/${slugify(card.set.series)}`}
                    className="text-white hover:text-white/80 transition-colors underline"
                  >
                    {card.set.series}
                  </Link>
                </p>
                <p><span className="font-semibold text-gray-400">Number:</span> {card.number}</p>
                <p><span className="font-semibold text-gray-400">Rarity:</span> {card.rarity}</p>
                <p>
                  <span className="font-semibold text-gray-400">Type:</span>{' '}
                  <span className="inline-flex items-center gap-2">
                    {card.types?.map((type, index) => (
                      <React.Fragment key={type}>
                        <Link
                          to={`/${slugify(card.set.series)}/${slugify(card.set.name)}?type=${type}`}
                          className="inline-flex items-center gap-1 text-white hover:text-white/80 transition-colors"
                        >
                          <EnergyIcon type={type} />
                          {type}
                        </Link>
                        {index < card.types.length - 1 && ', '}
                      </React.Fragment>
                    ))}
                  </span>
                </p>
                <p>
                  <span className="font-semibold text-gray-400">Release Date:</span>{' '}
                  {new Date(card.set.releaseDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                {card.evolves_from && (
                  <p>
                    <span className="font-semibold text-gray-400">Evolves From:</span>{' '}
                    {card.evolves_from}
                  </p>
                )}
                {card.evolves_to && card.evolves_to.length > 0 && (
                  <p>
                    <span className="font-semibold text-gray-400">Evolves To:</span>{' '}
                    {card.evolves_to.join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Market Information */}
          <div className="mt-6 bg-black/40 backdrop-blur-sm border border-white/10 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-white">Market Information</h3>
            <div className="space-y-6">
              {/* North American Market */}
              {shouldShowPriceSection({
                normal: card.tcg_normal_market,
                holofoil: card.tcg_holofoil_market,
                reverseHolo: card.tcg_reverse_holofoil_market,
                firstEdition: card.tcg_1st_edition_holofoil_market
              }) && (
                <div className="space-y-4">
                  <h4 className="font-medium text-white/80 flex items-center gap-2">
                    <US className="w-5 h-5" title="United States" />
                    United States Market
                    {card.tcgplayer_updated_at && (
                      <span className="text-xs text-white/40">
                        Updated {formatDate(card.tcgplayer_updated_at)}
                      </span>
                    )}
                  </h4>
                  
                  <div className="grid gap-3">
                    {/* Normal Prices */}
                    {card.tcg_normal_market > 0 && (
                      <div className="bg-white/5 border border-white/10 p-3 rounded-lg">
                        <h5 className="text-sm text-white/60 mb-2">Normal</h5>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {card.tcg_normal_market > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Market</p>
                              <p className="font-medium">{formatPrice(card.tcg_normal_market, currency)}</p>
                            </div>
                          )}
                          {card.tcg_normal_low > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Low</p>
                              <p className="font-medium">{formatPrice(card.tcg_normal_low, currency)}</p>
                            </div>
                          )}
                          {card.tcg_normal_mid > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Mid</p>
                              <p className="font-medium">{formatPrice(card.tcg_normal_mid, currency)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Holofoil Prices */}
                    {card.tcg_holofoil_market > 0 && (
                      <div className="bg-white/5 border border-white/10 p-3 rounded-lg">
                        <h5 className="text-sm text-white/60 mb-2">Holofoil</h5>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {card.tcg_holofoil_market > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Market</p>
                              <p className="font-medium">{formatPrice(card.tcg_holofoil_market, currency)}</p>
                            </div>
                          )}
                          {card.tcg_holofoil_low > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Low</p>
                              <p className="font-medium">{formatPrice(card.tcg_holofoil_low, currency)}</p>
                            </div>
                          )}
                          {card.tcg_holofoil_mid > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Mid</p>
                              <p className="font-medium">{formatPrice(card.tcg_holofoil_mid, currency)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Reverse Holofoil Prices */}
                    {card.tcg_reverse_holofoil_market > 0 && (
                      <div className="bg-white/5 border border-white/10 p-3 rounded-lg">
                        <h5 className="text-sm text-white/60 mb-2">Reverse Holofoil</h5>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {card.tcg_reverse_holofoil_market > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Market</p>
                              <p className="font-medium">{formatPrice(card.tcg_reverse_holofoil_market, currency)}</p>
                            </div>
                          )}
                          {card.tcg_reverse_holofoil_low > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Low</p>
                              <p className="font-medium">{formatPrice(card.tcg_reverse_holofoil_low, currency)}</p>
                            </div>
                          )}
                          {card.tcg_reverse_holofoil_mid > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Mid</p>
                              <p className="font-medium">{formatPrice(card.tcg_reverse_holofoil_mid, currency)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 1st Edition Holofoil Prices */}
                    {card.tcg_1st_edition_holofoil_market > 0 && (
                      <div className="bg-white/5 border border-white/10 p-3 rounded-lg">
                        <h5 className="text-sm text-white/60 mb-2">1st Edition Holofoil</h5>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {card.tcg_1st_edition_holofoil_market > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Market</p>
                              <p className="font-medium">{formatPrice(card.tcg_1st_edition_holofoil_market, currency)}</p>
                            </div>
                          )}
                          {card.tcg_1st_edition_holofoil_low > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Low</p>
                              <p className="font-medium">{formatPrice(card.tcg_1st_edition_holofoil_low, currency)}</p>
                            </div>
                          )}
                          {card.tcg_1st_edition_holofoil_mid > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Mid</p>
                              <p className="font-medium">{formatPrice(card.tcg_1st_edition_holofoil_mid, currency)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* European Market */}
              {shouldShowPriceSection({
                avg: card.cardmarket_avg_sell_price,
                suggested: card.cardmarket_suggested_price,
                reverseHolo: card.cardmarket_reverse_holo_sell
              }) && (
                <div className="space-y-4 mt-6">
                  <h4 className="font-medium text-white/80 flex items-center gap-2">
                    <EU className="w-5 h-5" title="European Union" />
                    European Market
                    {card.cardmarket_updated_at && (
                      <span className="text-xs text-white/40">
                        Updated {formatDate(card.cardmarket_updated_at)}
                      </span>
                    )}
                  </h4>

                  <div className="grid gap-3">
                    {/* Regular Prices */}
                    {(card.cardmarket_avg_sell_price > 0 || card.cardmarket_suggested_price > 0 || card.cardmarket_low_price_ex_plus > 0) && (
                      <div className="bg-white/5 border border-white/10 p-3 rounded-lg">
                        <h5 className="text-sm text-white/60 mb-2">Market Prices</h5>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {card.cardmarket_avg_sell_price > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Average</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_avg_sell_price, currency)}</p>
                            </div>
                          )}
                          {card.cardmarket_suggested_price > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Suggested</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_suggested_price, currency)}</p>
                            </div>
                          )}
                          {card.cardmarket_low_price_ex_plus > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Low (EX+)</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_low_price_ex_plus, currency)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Trend Prices */}
                    {(card.cardmarket_avg1 > 0 || card.cardmarket_avg7 > 0 || card.cardmarket_avg30 > 0) && (
                      <div className="bg-white/5 border border-white/10 p-3 rounded-lg">
                        <h5 className="text-sm text-white/60 mb-2">Price Trends</h5>
                        <div className="grid grid-cols-3 gap-2">
                          {card.cardmarket_avg1 > 0 && (
                            <div>
                              <p className="text-xs text-white/40">1 Day</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_avg1, currency)}</p>
                            </div>
                          )}
                          {card.cardmarket_avg7 > 0 && (
                            <div>
                              <p className="text-xs text-white/40">7 Days</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_avg7, currency)}</p>
                            </div>
                          )}
                          {card.cardmarket_avg30 > 0 && (
                            <div>
                              <p className="text-xs text-white/40">30 Days</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_avg30, currency)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Reverse Holo Prices */}
                    {(card.cardmarket_reverse_holo_sell > 0 || card.cardmarket_reverse_holo_avg1 > 0 || card.cardmarket_reverse_holo_avg7 > 0 || card.cardmarket_reverse_holo_avg30 > 0) && (
                      <div className="bg-white/5 border border-white/10 p-3 rounded-lg">
                        <h5 className="text-sm text-white/60 mb-2">Reverse Holo Prices</h5>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {card.cardmarket_reverse_holo_sell > 0 && (
                            <div>
                              <p className="text-xs text-white/40">Market</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_reverse_holo_sell, currency)}</p>
                            </div>
                          )}
                          {card.cardmarket_reverse_holo_avg1 > 0 && (
                            <div>
                              <p className="text-xs text-white/40">1 Day</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_reverse_holo_avg1, currency)}</p>
                            </div>
                          )}
                          {card.cardmarket_reverse_holo_avg7 > 0 && (
                            <div>
                              <p className="text-xs text-white/40">7 Days</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_reverse_holo_avg7, currency)}</p>
                            </div>
                          )}
                          {card.cardmarket_reverse_holo_avg30 > 0 && (
                            <div>
                              <p className="text-xs text-white/40">30 Days</p>
                              <p className="font-medium">{formatPrice(card.cardmarket_reverse_holo_avg30, currency)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <p className="text-xs text-white/40 mt-3 text-center">
                Prices updated daily from market data
              </p>
            </div>
          </div>
        </div>

        {/* Card Image Section - Now Last */}
        <div className="w-full lg:w-80 xl:w-96 mb-4 lg:mb-0 order-1 lg:order-last">
          <div className="relative max-w-[288px] mx-auto lg:mx-0">
            <CardImage
              src={imageUrl}
              alt={card.name}
              cardId={card.id}
              className="w-full rounded-lg shadow-md cursor-zoom-in"
              width={488}
              quality={90}
              loading="eager"
              fetchPriority="high"
              onClick={() => setIsZoomed(true)}
            />

            {/* Zoom overlay */}
            <button
              onClick={() => setIsZoomed(true)}
              className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors rounded-lg">
              <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
            </button>
          </div>

          {/* Collection Management Section */}
          <div className="mt-4 bg-black/40 backdrop-blur-sm border border-white/10 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">Add to Collection</h3>
            <div className="space-y-4">
              <div className="flex justify-center">
                <CollectionButton card={card} />
              </div>

              {/* Desktop: Always visible */}
              <div className="hidden lg:block text-sm text-white/60">
                <div className="flex items-start gap-2 mb-3">
                  <MousePointer size={16} className="text-white/40 mt-0.5 flex-shrink-0" />
                  <span>Left click to add, right click to remove</span>
                </div>
                <div className="flex items-start gap-2 mb-4">
                  <MousePointerClick size={16} className="text-white/40 mt-0.5 flex-shrink-0" />
                  <span>You can add multiple copies of each finish</span>
                </div>
                <p className="mb-2">Card Finishes:</p>
                <ul className="list-none space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500 mt-[0.4375rem] flex-shrink-0"></span>
                    <span><span className="text-yellow-500">Normal</span> - Regular card</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500 mt-[0.4375rem] flex-shrink-0"></span>
                    <span><span className="text-purple-500">Holofoil</span> - Traditional holo pattern</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500 mt-[0.4375rem] flex-shrink-0"></span>
                    <span><span className="text-blue-500">Reverse Holofoil</span> - Reverse holo pattern</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500 mt-[0.4375rem] flex-shrink-0"></span>
                    <span><span className="text-red-500">Poké Ball</span> - Special Poké Ball pattern</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-pink-500 mt-[0.4375rem] flex-shrink-0"></span>
                    <span><span className="text-pink-500">Master Ball</span> - Premium Master Ball pattern</span>
                  </li>
                </ul>
              </div>

              {/* Mobile: Collapsible section */}
              <div className="lg:hidden">
                <button
                  onClick={() => setIsHowItWorksOpen(!isHowItWorksOpen)}
                  className="w-full flex items-center justify-between text-sm text-white/60 hover:text-white transition-colors py-2"
                >
                  <span>How it works</span>
                  <ChevronDown
                    size={16}
                    className={`transform transition-transform ${isHowItWorksOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                
                {isHowItWorksOpen && (
                  <div className="text-sm text-white/60 pt-2 space-y-4">
                    <div className="flex items-start gap-2 mb-3">
                      <MousePointer size={16} className="text-white/40 mt-0.5 flex-shrink-0" />
                      <span>Left click to add, right click to remove</span>
                    </div>
                    <div className="flex items-start gap-2 mb-4">
                      <MousePointerClick size={16} className="text-white/40 mt-0.5 flex-shrink-0" />
                      <span>You can add multiple copies of each finish</span>
                    </div>
                    <p className="mb-2">Card Finishes:</p>
                    <ul className="list-none space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-500 mt-[0.4375rem] flex-shrink-0"></span>
                        <span><span className="text-yellow-500">Normal</span> - Regular card</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-purple-500 mt-[0.4375rem] flex-shrink-0"></span>
                        <span><span className="text-purple-500">Holofoil</span> - Traditional holo pattern</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500 mt-[0.4375rem] flex-shrink-0"></span>
                        <span><span className="text-blue-500">Reverse Holofoil</span> - Reverse holo pattern</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500 mt-[0.4375rem] flex-shrink-0"></span>
                        <span><span className="text-red-500">Poké Ball</span> - Special Poké Ball pattern</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-pink-500 mt-[0.4375rem] flex-shrink-0"></span>
                        <span><span className="text-pink-500">Master Ball</span> - Premium Master Ball pattern</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-8 space-y-6">
        {/* Abilities Section */}
        {card.abilities && card.abilities.length > 0 && (
          <div className="mt-6 sm:mt-8 max-w-[1200px]">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Zap className="text-yellow-500" size={20} />
              Abilities
            </h3>
            <div className="grid gap-2">
              {card.abilities.map((ability, index) => (
                <div key={index} className="bg-black/40 backdrop-blur-sm border border-white/10 p-4 rounded-lg">
                  <p className="font-semibold">{ability.name}</p>
                  <p className="text-sm text-gray-400 mt-1">{ability.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attacks Section */}
        {card.attacks && card.attacks.length > 0 && (
          <div className="mt-6 sm:mt-8 max-w-[1200px]">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Sword className="text-red-500" size={20} />
              Attacks
            </h3>
            <div className="grid gap-2">
              {card.attacks.map((attack, index) => (
                <div key={index} className="bg-black/40 backdrop-blur-sm border border-white/10 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {attack.cost?.map((energyType, i) => (
                        <EnergyIcon key={i} type={energyType} />
                      ))}
                      <p className="font-semibold">{attack.name}</p>
                    </div>
                    <p className="font-bold">{attack.damage || '—'}</p>
                  </div>
                  {attack.text && (
                    <p className="text-sm text-gray-400 mt-1">{attack.text}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Weaknesses Section */}
        {card.weaknesses && card.weaknesses.length > 0 && (
          <div className="mt-6 sm:mt-8 max-w-[1200px]">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Shield className="text-purple-500" size={20} />
              Weaknesses
            </h3>
            <div className="flex flex-wrap gap-2">
              {card.weaknesses.map((weakness, index) => (
                <span key={index} className="bg-black/40 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-full text-sm flex items-center gap-2">
                  <EnergyIcon type={weakness.type} />
                  {weakness.type} {weakness.value}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Retreat Cost Section */}
        {card.retreatCost && (
          <div className="mt-6 sm:mt-8 max-w-[1200px]">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <ArrowDownToLine className="text-gray-400" size={20} />
              Retreat Cost
            </h3>
            <div className="flex gap-2">
              {Array(card.retreatCost).fill(0).map((_, index) => (
                <EnergyIcon key={index} type="Colorless" />
              ))}
            </div>
          </div>
        )}
      </div>

      {isZoomed && (
        <CardZoom
          imageUrl={imageUrl}
          cardName={card.name}
          onClose={() => setIsZoomed(false)}
        />
      )}
    </div>
  );
}